import { useState, useEffect } from 'react';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';

const vanityMetrics = [
  {
    value: "50,000+",
    label: 'Active Users'
  },
  {
    value: "$2.3B",
    label: 'Home Value Tracked'
  },
  {
    value: "98%",
    label: 'Accuracy Rate'
  },
  {
    value: "4.9/5",
    label: 'User Rating'
  } 
]

const faq = [
  {
    question: 'How accurate are the home value estimates?',
    answer: 'Our estimates are powered by advanced AI algorithms that analyze millions of data points, including recent sales, market trends, and property-specific details. While no estimate can be 100% accurate without a professional appraisal, our estimates are typically within 5-10% of actual market value in most areas.'
  },
  {
    question: 'How often is my home value updated?',
    answer: 'We provide monthly updates to your home\'s estimated value, allowing you to track changes over time. Our system continuously monitors market conditions and automatically refreshes your valuation to reflect current trends.'
  },
  {
    question: 'What kind of refinance alerts will I receive?',
    answer: 'Our estimates are powered by advanced AI algorithms that analyze millions of data points, including recent sales, market trends, and property-specific details. While no estimate can be 100% accurate without a professional appraisal, our estimates are typically within 5-10% of actual market value in most areas.'
  },
  {
    question: 'Do I need any special technical knowledge to use HomePulse?',
    answer: 'Not at all! HomePulse is designed to be user-friendly and accessible. Our dashboard presents complex data in an easy-to-understand format, with actionable insights explained in plain language.'
  } 
]

const homeowners = [
  {
    rating: 5,
    review: 'HomePulse helped me time my refinance perfectly.  I saved over $50,000 in interest over the life of my loan!',
    name: 'Michael S.',
    title: 'Homeowner since 2018'
  },
  {
    rating: 5,
    review: 'I love seeing how my home value changes each month.  The renovation suggestions have been spot-on for my neighborhood.',
    name: 'Jennifer T.',
    title: 'First-time homeowner'
  },
  {
    rating: 5,
    review: 'After 10 years of owning, I finally feel like I understand the financial side of my home investment.  Incredible tool!',
    name: 'Robert K.',
    title: 'Long-term homeowner'
  }
]

const benefits = [
  {
    icon: 'far fa-house',
    title: 'Know What Your Home Is Worth',
    description: 'Monthly home value estimates powered by cutting-edge AI and the latest market data.',
    tip: 'Accurate, data-driven insights personalized to your address.'
  },
  {
    icon: 'far fa-lightbulb',
    title: 'Tips to Boost Value',
    description: 'Real-time suggestions on renovations and upgrades that pay off.',
    tip: 'Insights from local trends, not just generic advice.'
  },
  {
    icon: 'far fa-file-invoice-dollar',
    title: 'Smarter Mortgage Decisions',
    description: 'Instant alerts when it\'s a good time to refinance.',
    tip: 'See how much you could save in interest over time.'
  },
  {
    icon: 'far fa-chart-line',
    title: 'Stay Ahead of Market Shifts',
    description: 'The latest news and trends affecting your neighborhood\'s home values.',
    tip: 'Know if it\'s a buyer\'s or seller\'s market &emdash; before anyone else does.'
  },
  {
    icon: 'far fa-arrow-trend-up',
    title: 'Track Value Growth Over Time',
    description: 'Visualize your home\'s appreciation with easy-to-understand charts and data.',
    tip: 'Compare against neighborhood averages and historical trends.'
  },
  {
    icon: 'far fa-eye-slash',
    title: 'Stop Owning Blind',
    description: 'Think of it as your home\'s personal financial dashboard.',
    tip: 'Easy to use, always up-to-date, no jargon.'
  }
]

const steps = [
  {
    title: 'Enter Your Address',
    description: 'Provide your home address to get started with personalized tracking'
  },
  {
    title: 'Verify Your Ownership',
    description: 'Quick verification ensures accurate data and personalized&nbsp;insights'
  },
  {
    title: 'Get Monthly Updates',
    description: 'Receive regular value updates and actionable recommendations'
  }
]

const handleFaqClick = (index) => {
  const answer = document.querySelector(`#answer-${index}`);
  answer.classList.toggle('hidden');
}

function App() {
  const [user, setUser] = useState({email: null});
  const [property, setProperty] = useState(null);
  const [address, setAddress] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalEmail, setModalEmail] = useState('');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get('e');
    
    if (emailParam) {
      setUser({
        email: emailParam 
      });
    }
  }, []);

  const postToWebhook = async () => {
    setIsSubmitting(true);
    const payload = [{
      user: {
        email: user.email || modalEmail
      },
      property: {
        formatted_address: property.formattedAddress,
        place_id: property.placeId,
        latitude: property.latitude,
        longitude: property.longitude,
        address_components: property.addressComponents
      }
    }]
    console.log('Payload:', payload);
    const response = await fetch('https://movebuddha.app.n8n.cloud/webhook/cbc5b5b9-00c0-4091-a181-aa91e1b53cdf', {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json'
      }
    });
    setIsSubmitting(false);
    window.rdt('track', 'SignUp', {
      conversion_id: btoa(user.email || modalEmail)
    });
    window.twq('event', 'tw-qcdgj-qcuv8', {
    });
    return response.json();
  }

  const handleSelect = async (value) => {
    const results = await geocodeByAddress(value);
    const coordinates = await getLatLng(results[0]);
    console.log('Results:', results);
    console.log('Coordinates:', coordinates);
    setProperty({
      formattedAddress: results[0].formatted_address,
      placeId: results[0].place_id,
      latitude: coordinates.lat,
      longitude: coordinates.lng,
      addressComponents: results[0].address_components
    });
    setAddress(value);
  };

  const handleTrackHome = async () => {
    if (!address) {
      alert('Please enter an address first');
      return;
    }

    if(user.email) {
      postToWebhook();
    }

    setShowModal(true);
    document.body.style.overflow = 'hidden';
  };

  const handleUnlockHomeValue = async () => {
    if (!modalEmail) {
      alert('Please enter your email address');
      return;
    }

    if (!address) {
      alert('Please enter an address first');
      return;
    }

    setUser({
      email: modalEmail
    }); 

    postToWebhook();
  };

  const closeModal = () => {
    setShowModal(false);
    document.body.style.overflow = 'unset';
  };

  const searchOptions = {
    types: ['address'],
    componentRestrictions: { country: 'us' }
  };

  const hasEmailFromUrl = !!user.email;

  return (
    <>
    <header className="p-4 md:flex md:justify-between md:items-center bg-white" id="sign-up">
      <div className="font-bold text-xl text-center">
        <i className="far fa-wave-pulse mr-2 text-teal-700"></i>
        Home<span className="text-teal-700">Pulse</span>
      </div>
      <div className="hidden md:block">
        <ul className="flex gap-8">
          <li>
            Features
          </li>
          <li>
            Hot it Works
          </li>
          <li>
            Testimonials
          </li>
          <li>
            FAQ
          </li>
        </ul>
      </div>
      <div className="hidden md:block">
        <a href="#sign-up" className="px-4 py-2 rounded-md border-teal-700 text-teal-700 border rounded-md font-semibold">
          <i className="far fa-comment mr-2"></i>
          Contact Us
        </a>
      </div>
    </header>
    <main>
      <section style={{ backgroundImage: 'url(https://homepulsetracker.com/homepage.png)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="py-12 px-4" style={{ backgroundColor: 'rgba(240, 253, 250, 0.95)' }}>
      <h1 className="max-w-md mx-auto text-3xl md:text-4xl font-bold text-center mb-4 text-teal-900">Track your home value like your stock&nbsp;portfolio</h1>
      <p className="max-w-md mx-auto text-center text-teal-800 mb-8 text-lg leading-6">
        Get monthly home value updates, refinance alerts, and personalized recommendations to maximize&nbsp;your&nbsp;home's&nbsp;potential.
      </p>
      <div className="relative mb-8 max-w-xs mx-auto md:max-w-lg md:flex">
        <PlacesAutocomplete
          value={address}
          onChange={setAddress}
          onSelect={handleSelect}
          searchOptions={searchOptions}
        >
          {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
            <div className="w-full md:w-2/3 md:mb-0 mr-4 relative">
              <i className="far fa-map-marker-alt mr-2 absolute text-teal-700" style={{ top: '1.06125rem', left: '1rem' }}></i>
              <input
                {...getInputProps({
                  placeholder: 'Claim your home address',
                  className: 'w-full p-3 rounded-md border border-teal-700 pl-10 placeholder-teal-800 text-teal-800 md:border-2'
                })}
              />
              <div className="absolute z-10 w-full bg-white rounded-md shadow-lg max-h-60 overflow-auto">
                {loading && <div className="p-3 text-gray-900">Loading...</div>}
                {suggestions.map((suggestion) => {
                  const className = suggestion.active
                    ? 'p-3 cursor-pointer bg-teal-50 border-teal-700 border-b border-teal-50 text-teal-800'
                    : 'p-3 cursor-pointer hover:bg-gray-50 border-b border-teal-100 text-teal-800';
                  return (
                    <div
                      {...getSuggestionItemProps(suggestion, {
                        className
                      })}
                    >
                      <span>{suggestion.description}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </PlacesAutocomplete>
        <button 
          onClick={handleTrackHome}
          disabled={!address}
          className="px-4 py-3 shadow-md rounded-md w-full font-semibold md:w-1/3 mt-4 md:mt-0 transition-colors bg-teal-700 text-white hover:bg-teal-800"
        >
          Track My Home
        </button>
      </div>
      <div className="max-w-sm md:max-w-2xl mx-auto text-center justify-center gap-4 mb-8 px-4 md:flex md:justify-center md:text-sm">
        <div className="bg-white rounded-full text-center inline-block mb-4 flex items-center p-3 md:pr-6">
          <div>
            <span className="bg-teal-50 rounded-full w-8 h-8 p-2 mr-2 inline-block flex items-center justify-center">
            <i className="far fa-chart-line text-teal-700"></i>
          </span>
          </div>
          <span className="font-semibold">
            Monthly Value Updates
          </span>
        </div>
        <div className="bg-white rounded-full text-center inline-block mb-4 flex items-center p-3 md:pr-6">
          <div>
            <span className="bg-teal-50 rounded-full w-8 h-8 p-2 mr-2 inline-block flex items-center justify-center">
              <i className="far fa-bell text-teal-700"></i>
            </span>
          </div>
          <span className="font-semibold">
            Refinance Alerts
          </span>
        </div>
        <div className="bg-white rounded-full text-center inline-block mb-4 flex items-center p-3 md:pr-6">
          <div>
            <span className="bg-teal-50 rounded-full w-8 h-8 p-2 mr-2 inline-block flex items-center justify-center">
              <i className="far fa-lightbulb text-teal-700"></i>
            </span>
          </div>
          <span className="font-semibold">
            Value Boost Tips
          </span>
        </div>
      </div>
      <div className="text-center text-sm text-gray-700">
        <i className="fas fa-shield-check text-teal-700 mr-1"></i>
        Trusted by 50k+ homeowners
        <div className="flex justify-center gap-1 items-center mt-1">
          {Array.from({length: 5}).map((_, i) => (
            <i className="fas fa-star text-yellow-500"></i>
          ))} 4.9/5 Rating
        </div>
      </div>
        </div>
      </section>
      <section className="bg-white p-6 py-12">
        <h2 className="text-2xl font-bold mx-auto text-center md:text-3xl mb-4 max-w-lg text-teal-800">Everything you need to make smarter&nbsp;home&nbsp;decisions</h2>
        <p className="text-center text-lg text-teal-900 mb-8 leading-6 max-w-md mx-auto">
          HomePulse gives you the insights and tools to maximize your home's value and financial potential.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {benefits.map((benefit) => (
            <div className="p-6 border rounded-md shadow-sm">
              <span className="bg-teal-50 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <i className={`${benefit.icon} text-xl text-teal-700`}></i>
              </span>
              <div className="font-bold text-lg mb-2">
                {benefit.title}
              </div>
            <p className="text-gray-700 mb-4">
              {benefit.description}
              </p>
              <p className="text-gray-700 text-sm italic">
                {benefit.tip}
              </p>
            </div>
          ))}
        </div>
      </section>
      <section className="bg-teal-50 p-6 py-12">
        <div className="-mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 justify-center flex-wrap max-w-md lg:max-w-3xl mx-auto">
          {vanityMetrics.map((metric) => (
            <div className="text-center mb-2">
              <div className="text-2xl font-bold text-teal-700">
                {metric.value}                
              </div>
              <div className="text-xs text-teal-700">
                {metric.label}
              </div>
            </div>
          ))} 
        </div>
      </section>
      <section className="p-6 py-12 bg-white">
        <h2 className="text-2xl font-bold text-center mb-2 md:text-3xl text-teal-900">How HomePulse Works</h2>
        <p className="text-center text-lg text-teal-800 mb-12 leading-6">
          Just three simple steps to start making smarter decisions about your home.
        </p>
        <div className="mb-4 grid grid-cols-1 2xl:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {steps.map((step, i) => (
            <div className="text-center mb-8">
              <span className="bg-teal-700 text-white rounded-full w-10 h-10 flex items-center justify-center mb-4 text-md inline-block mx-auto">
                {i+1}
              </span>
              <h3 className="text-lg font-bold mb-2 text-teal-900">{step.title}</h3>
              <p className="text-teal-800 max-w-64 mx-auto" dangerouslySetInnerHTML={{ __html: step.description }}></p>
            </div>
          ))}
        </div>  
        <a href="#sign-up" className="max-w-fit bg-teal-700 text-white px-8 py-3 shadow-md rounded-md font-semibold text-center block mx-auto">Start Tracking My Home <i className="far fa-arrow-right ml-2"></i></a>
      </section>
      <section className="p-6 py-12 bg-teal-50">
        <h2 className="text-2xl font-bold text-center mb-2 text-teal-900 md:text-3xl">What Homeowners Say</h2>
        <p className="text-center text-lg text-teal-800 mb-8 leading-6">
          Join thousands of happy homeowners who are making smarter decisions with&nbsp;HomePulse
        </p>
        <div className="-mb-8 grid grid-cols-1 2xl:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {homeowners.map((homeowner) => (
            <div className="mb-8 p-6 rounded-md shadow-lg bg-white">
              <div className="mb-4">
              {Array.from({length: homeowner.rating}).map((_, i) => (
                <i className="fas fa-star text-yellow-500"></i>
              ))}
              </div>
              <p className="mb-4 italic">{homeowner.review}</p>
              <div>
                <div className="font-bold">{homeowner.name}</div>
                <div className="text-gray-600 text-sm">{homeowner.title}</div>
              </div>
            </div>
          ))}
        </div>
      </section>
      <section className="p-8 py-12 bg-white">
        <h2 className="text-2xl font-bold text-center mb-2 text-teal-900">Frequently Asked Questions</h2>
        <p className="text-center text-lg text-teal-800 mb-10 leading-6">
          Everything you need to know about HomePulse
        </p>
        <ul className="accordion max-w-xl mx-auto">
          {faq.map((item, index) => (
            <li className="mb-6 relative rounded flex justify-between border-b border-gray-200 pb-6" key={index} onClick={() => handleFaqClick(index)}>
              <div>
                <h3 className="font-semibold leading-5">{item.question}</h3>
                <p className="text-gray-700 hidden mt-4" id={`answer-${index}`}>{item.answer}</p>
              </div>
              <div className="pl-4">
                <i className="fas fa-chevron-down text-gray-500"></i>
              </div>
            </li>
          ))}
        </ul>
      </section>
      <section className="p-6 py-16 bg-teal-700 text-white text-center">
        <h2 className="text-3xl font-bold text-center mb-2">Ready to unlock your home's full potential?</h2>
        <p className="text-lg mb-8 opacity-60 max-w-xl mx-auto leading-6">
          Join thousands of homeowners who are tracking their property's value and making smarter financial decisions.
        </p>
        <a href="#sign-up" className="max-w-fit bg-white text-teal-700 px-8 py-3 shadow-md rounded-md font-semibold text-center block mx-auto">Start Tracking Now <i className="far fa-arrow-right ml-2"></i></a>
      </section>
    </main>
    <footer className="p-8 bg-gray-100 text-gray-700 bg-sky-950 text-sky-50">
      <div className="grid grid-cols-1 2xl:grid-cols-4 gap-8">
      <div className="mb-8">
        <h4 className="font-bold text-lg mb-4"><i className="far fa-wave-pulse mr-1"></i> HomePulse</h4>
        <p className="text-sm opacity-60 max-w-xs">
          Helping homeowners make smarter decisions about their biggest investment.
        </p>
      </div>
      <div className="text-sm mb-8">
        <h4 className="font-bold text-base mb-4">Product</h4>
        <ul className="opacity-60">
          <li className="mb-2">
            <a href="#features">Features</a>
          </li>
          <li className="mb-2">
            <a href="#how-it-works">How it Works</a>
          </li>
          <li className="mb-2">
            <a href="#pricing">Pricing</a>
          </li>
          <li>
            <a href="#testimonials">Testimonials</a>
          </li>
        </ul>
      </div>
      <div className="text-sm mb-8">
        <h4 className="font-bold text-base mb-4">Resources</h4>
        <ul className="opacity-60">
          <li className="mb-2">
            <a href="#blog">Blog</a>
          </li>
          <li className="mb-2">
            <a href="#home-value-guide">Home Value Guide</a>
          </li>
          <li className="mb-2">
            <a href="#market-reports">Market Reports</a>
          </li>
          <li>
            <a href="#help-center">Help Center</a>
          </li>
        </ul>
      </div>
      <div className="text-sm mb-8">
        <h4 className="font-bold text-base mb-4">Company</h4>
        <ul className="opacity-60">
          <li className="mb-2">
            <a href="#about-us">About Us</a>
          </li>
          <li className="mb-2">
            <a href="#contact">Contact</a>
          </li>
          <li className="mb-2">
            <a href="#careers">Careers</a>
          </li>
          <li className="mb-2">
            <a href="/privacy-policy">Privacy Policy</a>
          </li>
          <li>
            <a href="/terms-of-service">Terms of Service</a>
          </li>
        </ul>
      </div>
      </div>
      <div className="text-xs opacity-60">
          &copy; 2024 HomePulse. All rights reserved.
      </div>
    </footer>

    {/* Modal */}
    {showModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
          <button
            onClick={closeModal}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-xl"
          >
            <i className="fas fa-times"></i>
          </button>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="far fa-chart-line text-2xl text-teal-700"></i>
            </div>
            
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              {hasEmailFromUrl ? "We're Getting to Work!" : "Unlock Your Home Value"}
            </h3>
            
            {hasEmailFromUrl ? (
              <>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Our team is now analyzing your home at <strong>{address}</strong> and gathering the latest market data. We'll email your personalized home value report to <strong>{user.email}</strong> within 24 hours.
                </p>
                
                <div className="bg-teal-50 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-center space-x-2 text-sm text-teal-700">
                    <i className="fas fa-clock"></i>
                    <span>Report will be ready in 24 hours</span>
                  </div>
                </div>
                
                <button
                  onClick={closeModal}
                  className="bg-teal-700 text-white px-6 py-2 rounded-md font-semibold hover:bg-teal-800 transition-colors"
                >
                  Got it!
                </button>
              </>
            ) : (
              <>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Enter your email address to receive your personalized home value report for <strong>{address}</strong> within 24 hours.
                </p>
                
                <div className="mb-6">
                  <input
                    type="email"
                    value={modalEmail}
                    onChange={(e) => setModalEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div className="bg-teal-50 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-center space-x-2 text-sm text-teal-700">
                    <i className="fas fa-shield-check"></i>
                    <span>We'll never share your email with anyone</span>
                  </div>
                </div>
                
                <button
                  onClick={handleUnlockHomeValue}
                  disabled={isSubmitting || !modalEmail}
                  className={`px-6 py-2 rounded-md font-semibold transition-colors ${
                    isSubmitting || !modalEmail
                      ? 'bg-teal-700 text-white cursor-not-allowed'
                      : 'bg-teal-700 text-white hover:bg-teal-800 cursor-pointer'
                  }`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Processing...
                    </span>
                  ) : (
                    'Unlock Your Home Value'
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
}

export default App;
